import { useState, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem, selectActiveDsChat } from 'redux/db/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import useScrollMemory from 'hooks/useScrollMemory';
import { useDsChatQueue } from 'hooks/useDsChatQueue';
import { ChatMode, DesignSystemFont, UserChatEntry } from 'dragonmouse-shared/interfaces';
import { DesignSystemToken } from 'designSystem/types';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import TypographySection from '../tokens/TypographySection';
import FontLoader from '../tokens/TypographySection/FontLoader';
import FontList from './FontList';
import * as Base from '../styles';
import * as S from './styles';

const DEFAULT_PREVIEW = 'The quick brown fox jumps over the lazy dog';

const TypographyView = () => {
	const { designSystemContent, activeDesignSystem, activeTab, dsChat } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeTab: selectActiveDsTab(r.mode),
			dsChat: selectActiveDsChat(r.db, r.mode.activeDsChatId),
		}),
		shallowEqual
	);
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	const fonts = designSystemContent?.config?.fonts;

	// Sticky preview controls — own them at the page level so every specimen
	// below renders against the same input.
	const [previewText, setPreviewText] = useState(DEFAULT_PREVIEW);
	const [dark, setDark] = useState(false);
	const displayText = previewText || DEFAULT_PREVIEW;

	const queue = useDsChatQueue(dsChat?.id ?? null);
	const fontReconcileKey = 'typography:reconcile-fonts';
	const isFontReconcileQueued = queue.items.some((e) => e.clientKey === fontReconcileKey);
	const missingFonts = designSystemContent?.missingFonts;

	// Combine theme + styles typography tokens, base only (no mode overrides)
	const typographyTokens = useMemo(() => {
		const themeTokens = designSystemContent?.theme?.byCategory?.typography ?? [];
		const stylesTokens = designSystemContent?.styles?.tokens?.byCategory?.typography ?? [];
		const all: DesignSystemToken[] = [...themeTokens, ...stylesTokens];
		return all.filter((t) => !t.mode);
	}, [
		designSystemContent?.theme?.byCategory?.typography,
		designSystemContent?.styles?.tokens?.byCategory?.typography,
	]);

	const onFontsCommit = async (newFonts: DesignSystemFont[]) => {
		if (!activeDesignSystem?.id) return;
		try {
			await forge.updateDsFiles({
				designSystemId: activeDesignSystem.id,
				files: [{ path: 'config/fonts.json', content: JSON.stringify(newFonts) }],
			});
		} catch (err) {
			Sentry.captureException(err);
			toast.error('Failed to update fonts');
		}
	};

	const onReconcileFonts = () => {
		if (!dsChat?.id || !missingFonts?.length || isFontReconcileQueued) return;

		const message = [
			`The following fonts are used in theme CSS but not configured in fonts.json: ${missingFonts.join(
				', '
			)}.`,
			'Please update typography tokens or font configuration to resolve the mismatch.',
		].join(' ');

		const entry: UserChatEntry = {
			kind: 'user',
			message,
			attachments: [],
			sourceFrameIds: [],
		};
		queue.enqueue({
			label: 'Reconcile fonts',
			clientKey: fontReconcileKey,
			payload: { kind: 'addUserEntry', entry, chatMode: ChatMode.DESIGN_AND_CHAT },
		});
	};

	return (
		<S.ScrollContainer ref={scrollRef}>
			<FontLoader />
			<S.HeaderArea>
				<Base.SectionHeader>
					<Base.SectionTitle>Typography</Base.SectionTitle>
				</Base.SectionHeader>
			</S.HeaderArea>
			<S.PreviewBar>
				<S.PreviewBarLabel>Sample text</S.PreviewBarLabel>
				<S.PreviewInput
					type="text"
					value={previewText}
					onChange={(e) => setPreviewText(e.target.value)}
					placeholder={DEFAULT_PREVIEW}
				/>
				<S.BgToggleLight
					type="button"
					$active={!dark}
					onClick={() => setDark(false)}
					title="Light background"
				/>
				<S.BgToggleDark
					type="button"
					$active={dark}
					onClick={() => setDark(true)}
					title="Dark background"
				/>
			</S.PreviewBar>
			<Base.SectionContent>
				<Base.SectionDivider>Fonts</Base.SectionDivider>
				{missingFonts && missingFonts.length > 0 && (
					<S.FontWarningBanner>
						<S.FontWarningText>
							The theme uses the following missing {missingFonts.length > 1 ? 'fonts' : 'font'}:
							{` `}
							<S.FontWarningFontList>{missingFonts.join(', ')}</S.FontWarningFontList>
						</S.FontWarningText>
						{dsChat && (
							<S.FontWarningAction
								onClick={onReconcileFonts}
								disabled={isFontReconcileQueued}
								$queued={isFontReconcileQueued}
							>
								{isFontReconcileQueued ? 'Reconcile · queued' : 'Reconcile'}
							</S.FontWarningAction>
						)}
					</S.FontWarningBanner>
				)}
				{fonts && activeDesignSystem?.id && (
					<FontList
						fonts={fonts}
						designSystemId={activeDesignSystem.id}
						previewText={displayText}
						dark={dark}
						onCommit={onFontsCommit}
					/>
				)}

				<TypographySection tokens={typographyTokens} previewText={displayText} dark={dark} />
			</Base.SectionContent>
		</S.ScrollContainer>
	);
};

export default TypographyView;
